/**
 * Filter utilities for the audit logs table.
 */

import type { CalendarDate } from '@internationalized/date';
import { today, getLocalTimeZone, parseDate } from '@internationalized/date';
import type { AuditLogFilters } from '../../types';
import { parseObjectTypeActionValue } from '../../utils/object-type-mapping';

// Constants for "all" filter values
export const ALL_USERS_VALUE = 'all';
export const ALL_OBJECT_TYPES_VALUE = 'all';

// UI constants
// Note: These represent the number of days to display in the UI (e.g., "Last 30 days").
// The actual date calculation subtracts (N - 1) days because "Last N days" includes today.
// Example: "Last 30 days" on Dec 11 = Nov 12 to Dec 11 (30 calendar days inclusive).
export const DEFAULT_DATE_RANGE_DAYS = 30;
export const LOADING_SKELETON_ROW_COUNT = 5;

// FilterValue type definition
export type FilterValue = {
  key: string;
  value: unknown;
  isDisabled?: boolean;
};

/**
 * Get the default date range (last N days, including today).
 * "Last 30 days" means 30 calendar days: today is day 1, so we subtract (N - 1).
 * Example: On Dec 11, "Last 30 days" = Nov 12 (day 30) to Dec 11 (day 1).
 */
export function getDefaultDateRange(): { start: CalendarDate; end: CalendarDate } {
  const endDate = today(getLocalTimeZone());
  const startDate = endDate.subtract({ days: DEFAULT_DATE_RANGE_DAYS - 1 });
  return { start: startDate, end: endDate };
}

/**
 * Validate and clamp date range for accounts with limited access.
 * Ensures:
 * 1. End date is not in the future
 * 2. Start date is not earlier than the day limit ago
 * 3. If the entire range is outside the allowed window, resets to default
 * 4. Prevents invalid inverted ranges (start > end)
 * 5. Handles partial date ranges (only start or only end provided)
 *
 * @param dateRange - The date range to validate
 * @param dayLimit - The maximum number of days allowed (null means unlimited)
 * @returns A new date range object with validated/clamped dates (never mutates input)
 */
export function validateDateRangeForNonPremium(
  dateRange: { start?: CalendarDate; end?: CalendarDate },
  dayLimit: number | null,
): { start?: CalendarDate; end?: CalendarDate } {
  // Accounts with unlimited access have no restrictions
  if (dayLimit == null) {
    return { start: dateRange.start, end: dateRange.end };
  }

  // If both are null, return as-is
  if (dateRange.start == null && dateRange.end == null) {
    return { start: dateRange.start, end: dateRange.end };
  }

  const todayDate = today(getLocalTimeZone());
  // "Last N days" includes today, so min allowed = today - (N - 1)
  const minAllowedDate = todayDate.subtract({ days: dayLimit - 1 });

  // Handle partial date ranges
  if (dateRange.start == null && dateRange.end != null) {
    // Only end date provided - clamp end and set start to N days before (or minAllowedDate)
    let clampedEnd = dateRange.end;
    if (clampedEnd.compare(todayDate) > 0) {
      clampedEnd = todayDate;
    }
    if (clampedEnd.compare(minAllowedDate) < 0) {
      // End is too old, reset to default
      return getDefaultDateRange();
    }
    // Set start to max days before end, but not earlier than minAllowedDate
    // "Last N days" includes both endpoints, so subtract (N - 1)
    const calculatedStart = clampedEnd.subtract({ days: dayLimit - 1 });
    const clampedStart = calculatedStart.compare(minAllowedDate) < 0 ? minAllowedDate : calculatedStart;
    return { start: clampedStart, end: clampedEnd };
  }

  if (dateRange.end == null && dateRange.start != null) {
    // Only start date provided - clamp start and set end to today
    let clampedStart = dateRange.start;
    if (clampedStart.compare(minAllowedDate) < 0) {
      clampedStart = minAllowedDate;
    }
    if (clampedStart.compare(todayDate) > 0) {
      // Start is in the future, clamp to today
      clampedStart = todayDate;
    }
    return { start: clampedStart, end: todayDate };
  }

  // Both dates provided - validate with improved order to prevent inverted ranges
  // TypeScript: We know both are not null from the checks above
  let clampedEnd: CalendarDate = dateRange.end as CalendarDate;
  let clampedStart: CalendarDate = dateRange.start as CalendarDate;

  // Step 1: Clamp end date to today (prevent future dates)
  if (clampedEnd.compare(todayDate) > 0) {
    clampedEnd = todayDate;
  }

  // Step 2: If end date is before the minimum allowed date, entire range is invalid
  // Reset to default range to avoid invalid state
  if (clampedEnd.compare(minAllowedDate) < 0) {
    return getDefaultDateRange();
  }

  // Step 3: Clamp start date to minimum allowed date
  if (clampedStart.compare(minAllowedDate) < 0) {
    clampedStart = minAllowedDate;
  }

  // Step 4: Ensure start is not after end (prevents inverted ranges)
  // This check happens after all clamping to catch any edge cases
  if (clampedStart.compare(clampedEnd) > 0) {
    clampedStart = clampedEnd;
  }

  // Return a new object with clamped values instead of mutating the input
  return { start: clampedStart, end: clampedEnd };
}

/**
 * Build API filters from filter values array.
 *
 * @param filterValues - Array of filter key-value pairs from UI state
 * @param dayLimit - Max days allowed for date range (null = unlimited)
 * @param uiObjectType - UI-only object type to distinguish Campaign vs APITriggeredBroadcast
 *                       Used to set backend campaign_type filter for server-side filtering
 */
export function buildFiltersFromValues(
  filterValues: FilterValue[],
  dayLimit: number | null,
  _uiObjectType?: string | null,
): Partial<AuditLogFilters> {
  const newFilters: Partial<AuditLogFilters> = { cursor: undefined };

  for (const filter of filterValues) {
    const filterValue = filter.value;
    if (filterValue === '' || filterValue == null) continue;

    switch (filter.key) {
      case 'user_id': {
        if (typeof filterValue === 'string' && filterValue === ALL_USERS_VALUE) {
          newFilters.user_id = undefined;
          break;
        }
        const filterValueString: string = typeof filterValue === 'string' ? filterValue : String(filterValue);
        const parsedUserId = Number.parseInt(filterValueString, 10);
        if (!Number.isNaN(parsedUserId)) {
          newFilters.user_id = parsedUserId;
        }
        break;
      }
      case 'ip_addr':
        newFilters.ip_addr = typeof filterValue === 'string' ? filterValue : String(filterValue);
        break;
      case 'object_type': {
        newFilters.attribute_name = undefined;

        // Handle "All" selection - clear object_type (derived from ui_object_type in validatedFilters)
        if (typeof filterValue === 'string' && filterValue === ALL_OBJECT_TYPES_VALUE) {
          newFilters.object_type = undefined;
          newFilters.action = undefined;
          newFilters.object_id = undefined;
          break;
        }

        // Handle combined format "ObjectType:action" (e.g., "User:updated", "Campaign:created")
        const filterValueString = typeof filterValue === 'string' ? filterValue : String(filterValue);
        const parsed = parseObjectTypeActionValue(filterValueString);
        const objectType = parsed?.objectType ?? filterValueString;
        const action = parsed?.action;

        // For Campaign/APITriggeredBroadcast without a specific action, don't set object_type
        // It will be derived from ui_object_type in validatedFilters - keeps URL simpler
        if ((objectType === 'Campaign' || objectType === 'APITriggeredBroadcast') && action == null) {
          newFilters.object_type = undefined;
          newFilters.action = undefined;
          break;
        }

        // For other object types, or when a specific action is selected, set object_type
        // Map APITriggeredBroadcast to Campaign for the backend
        newFilters.object_type = objectType === 'APITriggeredBroadcast' ? 'Campaign' : objectType;
        newFilters.action = action;
        break;
      }
      case 'action': {
        // Handle action filter separately
        if (filterValue === '' || filterValue == null) {
          newFilters.action = undefined;
        } else {
          newFilters.action = typeof filterValue === 'string' ? filterValue : String(filterValue);
        }
        break;
      }
      case 'object_id':
        newFilters.object_id = typeof filterValue === 'string' ? filterValue : String(filterValue);
        break;
      case 'attribute_name':
        newFilters.attribute_name = typeof filterValue === 'string' ? filterValue : String(filterValue);
        break;
      case 'date_range': {
        // Handle date range filter - value is { start?: CalendarDate, end?: CalendarDate }
        let dateRange = filterValue as { start?: CalendarDate; end?: CalendarDate };
        // Validate and clamp date range for accounts with limited access
        dateRange = validateDateRangeForNonPremium(dateRange, dayLimit);
        if (dateRange.start != null) {
          newFilters.start_time = `${dateRange.start.toString()}T00:00:00Z`;
        }
        if (dateRange.end != null) {
          newFilters.end_time = `${dateRange.end.toString()}T23:59:59Z`;
        }
        break;
      }
      case 'campaign_id': {
        // Handle campaign_id filter - only applicable for CampaignAction object type
        const filterValueString: string = typeof filterValue === 'string' ? filterValue : String(filterValue);
        const parsedCampaignId = Number.parseInt(filterValueString, 10);
        if (!Number.isNaN(parsedCampaignId)) {
          newFilters.campaign_id = parsedCampaignId;
        }
        break;
      }
    }
  }

  return newFilters;
}

/**
 * Initialize filter values from existing filters.
 * @param filters - The audit log filters from API/URL
 * @param dayLimit - The day limit for date validation
 * @param uiObjectType - Optional UI-only object type (e.g., 'APITriggeredBroadcast') to preserve UI selection
 */
export function initializeFilterValues(
  filters: AuditLogFilters,
  dayLimit: number | null,
  uiObjectType?: string | null,
): FilterValue[] {
  const values: FilterValue[] = [];

  // Always-visible filter: Date Range (default: last 30 days)
  const startTimeString: string | undefined = typeof filters.start_time === 'string' ? filters.start_time : undefined;
  const endTimeString: string | undefined = typeof filters.end_time === 'string' ? filters.end_time : undefined;
  const hasStartTime = typeof startTimeString === 'string' && startTimeString.trim() !== '';
  const hasEndTime = typeof endTimeString === 'string' && endTimeString.trim() !== '';
  if (hasStartTime || hasEndTime) {
    let dateRangeValue: { start?: CalendarDate; end?: CalendarDate } = {};
    if (hasStartTime && startTimeString !== undefined) {
      try {
        const dateString = startTimeString.split('T')[0];
        dateRangeValue.start = parseDate(dateString);
      } catch {
        // ignore invalid date
      }
    }
    if (hasEndTime && endTimeString !== undefined) {
      try {
        const dateString = endTimeString.split('T')[0];
        dateRangeValue.end = parseDate(dateString);
      } catch {
        // ignore invalid date
      }
    }
    if (dateRangeValue.start != null || dateRangeValue.end != null) {
      // Validate and clamp date range for accounts with limited access
      dateRangeValue = validateDateRangeForNonPremium(dateRangeValue, dayLimit);
      values.push({ key: 'date_range', value: dateRangeValue });
    } else {
      values.push({ key: 'date_range', value: getDefaultDateRange() });
    }
  } else {
    // Default to last 30 days
    values.push({ key: 'date_range', value: getDefaultDateRange() });
  }

  // Always-visible filter: User (default: all users)
  if (typeof filters.user_id === 'number') {
    values.push({ key: 'user_id', value: filters.user_id.toString() });
  } else {
    values.push({ key: 'user_id', value: ALL_USERS_VALUE });
  }

  // Always-visible filter: Object Type (default: all object types)
  // Note: Backend uses 'Campaign' for both Campaign and API-Triggered Broadcast
  // We use ui_object_type URL parameter to preserve UI selection
  const objectTypeString: string | undefined =
    typeof filters.object_type === 'string' ? filters.object_type : undefined;
  if (typeof objectTypeString === 'string' && objectTypeString.trim() !== '') {
    const actionString: string | undefined = typeof filters.action === 'string' ? filters.action : undefined;

    // If ui_object_type is set to 'APITriggeredBroadcast', use that for UI instead of 'Campaign'
    // This preserves the UI selection when reading from URL
    const uiType = uiObjectType != null && uiObjectType !== '' ? uiObjectType : objectTypeString;
    const combinedValue =
      typeof actionString === 'string' && actionString.trim() !== '' ? `${uiType}:${actionString}` : uiType;
    values.push({ key: 'object_type', value: combinedValue });
  } else if (uiObjectType != null && uiObjectType !== '') {
    // When object_type is empty but uiObjectType is set (e.g., Campaign/APITriggeredBroadcast without action),
    // use uiObjectType to preserve the UI selection instead of defaulting to "All"
    values.push({ key: 'object_type', value: uiObjectType });
  } else {
    values.push({ key: 'object_type', value: ALL_OBJECT_TYPES_VALUE });
  }

  // Other filters (only added when they have values)
  const ipAddrString: string | undefined = typeof filters.ip_addr === 'string' ? filters.ip_addr : undefined;
  if (typeof ipAddrString === 'string' && ipAddrString.trim() !== '') {
    values.push({ key: 'ip_addr', value: ipAddrString });
  }
  const objectIdString: string | undefined = typeof filters.object_id === 'string' ? filters.object_id : undefined;
  if (typeof objectIdString === 'string' && objectIdString.trim() !== '') {
    values.push({ key: 'object_id', value: objectIdString });
  }
  const attributeNameString: string | undefined =
    typeof filters.attribute_name === 'string' ? filters.attribute_name : undefined;
  if (typeof attributeNameString === 'string' && attributeNameString.trim() !== '') {
    values.push({ key: 'attribute_name', value: attributeNameString });
  }
  // Campaign ID filter (only applicable for CampaignAction object type)
  if (typeof filters.campaign_id === 'number') {
    values.push({ key: 'campaign_id', value: filters.campaign_id.toString() });
  }

  return values;
}

/**
 * Validate and clamp date range in AuditLogFilters for accounts with limited access.
 * This ensures API calls respect the day limit even when filters come from URL parameters.
 *
 * @param filters - The filters to validate
 * @param dayLimit - The maximum number of days allowed (null means unlimited)
 * @returns A new filters object with validated/clamped dates (never mutates input)
 */
export function validateFiltersForNonPremium(filters: AuditLogFilters, dayLimit: number | null): AuditLogFilters {
  // Accounts with unlimited access have no restrictions - but still return new object for consistency
  if (dayLimit == null) {
    return { ...filters };
  }

  // If no date filters, return new object (never mutate input)
  const startTimeString: string | undefined = typeof filters.start_time === 'string' ? filters.start_time : undefined;
  const endTimeString: string | undefined = typeof filters.end_time === 'string' ? filters.end_time : undefined;
  const hasStartTime = typeof startTimeString === 'string' && startTimeString.trim() !== '';
  const hasEndTime = typeof endTimeString === 'string' && endTimeString.trim() !== '';

  if (!hasStartTime && !hasEndTime) {
    return { ...filters };
  }

  // Parse string dates to CalendarDate
  const dateRange: { start?: CalendarDate; end?: CalendarDate } = {};
  if (hasStartTime && startTimeString !== undefined) {
    try {
      const dateString = startTimeString.split('T')[0];
      dateRange.start = parseDate(dateString);
    } catch {
      // Invalid date - will be handled by validation
    }
  }
  if (hasEndTime && endTimeString !== undefined) {
    try {
      const dateString = endTimeString.split('T')[0];
      dateRange.end = parseDate(dateString);
    } catch {
      // Invalid date - will be handled by validation
    }
  }

  // Validate the date range
  const validatedRange = validateDateRangeForNonPremium(dateRange, dayLimit);

  // Convert back to string format and return new filters object
  const validatedFilters: AuditLogFilters = { ...filters };
  validatedFilters.start_time =
    validatedRange.start != null ? `${validatedRange.start.toString()}T00:00:00Z` : undefined;
  validatedFilters.end_time = validatedRange.end != null ? `${validatedRange.end.toString()}T23:59:59Z` : undefined;

  return validatedFilters;
}
